/**
 * The local half of a two-pane file manager, in a browser.
 *
 * FileZilla's left pane is the operator's own filesystem, and that is the thing
 * a web page classically cannot have. It can now, in some browsers: the File
 * System Access API hands back a real directory handle that can be listed, read
 * from and written into, with the operator's explicit consent and nothing
 * persisted unless they say so.
 *
 * So there are two modes, and the interface says which one it is in rather than
 * pretending they are the same:
 *
 *   - **A chosen folder** (`directory`). Chromium desktop. Genuinely two panes:
 *     browse the local tree, drag either way, and a download lands *in that
 *     folder* rather than wherever the browser dumps things.
 *   - **A tray** (`tray`). Firefox, Safari, anything mobile. Files are picked or
 *     dropped and queue up to be sent; downloads go to the browser's own
 *     download location, because nothing else is reachable.
 *
 * The fallback is not a degraded version of the same screen — it is a different
 * and smaller promise, and it is labelled as one. Saying "local files" over a
 * list the page cannot actually browse would be the lie this module is being
 * rebuilt to stop telling.
 */

/** What the browser will let this page do with the operator's own disk. */
export type LocalMode = 'directory' | 'tray'

export function localMode(): LocalMode {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window ? 'directory' : 'tray'
}

/* The File System Access API is not in this project's TypeScript lib, so the
   slice that is used is declared here rather than pulling a dependency for a
   handful of method signatures. Narrow on purpose: a wider declaration would be
   a claim about an API surface this code does not exercise. */
export interface LocalFileHandle {
  kind: 'file'
  name: string
  getFile(): Promise<File>
  createWritable(): Promise<{
    write(data: ArrayBufferView | ArrayBuffer | Blob | string): Promise<void>
    close(): Promise<void>
    abort?(): Promise<void>
  }>
}

export interface LocalDirHandle {
  kind: 'directory'
  name: string
  values(): AsyncIterableIterator<LocalFileHandle | LocalDirHandle>
  getDirectoryHandle(name: string, opts?: { create?: boolean }): Promise<LocalDirHandle>
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<LocalFileHandle>
  queryPermission?(opts: { mode: 'read' | 'readwrite' }): Promise<PermissionState>
  requestPermission?(opts: { mode: 'read' | 'readwrite' }): Promise<PermissionState>
}

declare global {
  interface Window {
    showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite' }) => Promise<LocalDirHandle>
  }
}

/** One row in the local pane. */
export interface LocalEntry {
  name: string
  dir: boolean
  size: number
  /** Epoch milliseconds, or 0 when the platform did not report one. Zero is
   *  rendered as "—" rather than as 1970: unknown is not a date. */
  modified: number
  file?: LocalFileHandle
  directory?: LocalDirHandle
}

/**
 * Asks for a folder, with write permission.
 *
 * `readwrite` up front rather than read-first-then-upgrade, because the second
 * prompt would arrive at the moment a download starts — which is the worst
 * moment to interrupt somebody, and the one where a refusal loses work already
 * in flight.
 *
 * Returns null when the operator cancels the picker, which is not an error and
 * must not be reported as one.
 */
export async function pickLocalDirectory(): Promise<LocalDirHandle | null> {
  if (!window.showDirectoryPicker) return null
  try {
    return await window.showDirectoryPicker({ mode: 'readwrite' })
  } catch (err) {
    // AbortError is the operator closing the picker. Anything else is worth
    // surfacing, so it is rethrown rather than swallowed with it.
    if (err instanceof DOMException && err.name === 'AbortError') return null
    throw err
  }
}

/** Confirms the handle still carries write permission, prompting if it does not. */
export async function ensureWritable(dir: LocalDirHandle): Promise<boolean> {
  if (!dir.queryPermission) return true
  if ((await dir.queryPermission({ mode: 'readwrite' })) === 'granted') return true
  if (!dir.requestPermission) return false
  return (await dir.requestPermission({ mode: 'readwrite' })) === 'granted'
}

/**
 * Lists one local directory.
 *
 * Sizes and dates come from the `File` behind each handle, which means one
 * `getFile()` per entry. That is the only way the API offers, and it is why a
 * local directory of ten thousand files is slow — a real cost, stated here so
 * the next reader does not go looking for a batch call that does not exist.
 */
export async function readLocalDir(dir: LocalDirHandle): Promise<LocalEntry[]> {
  const out: LocalEntry[] = []
  for await (const handle of dir.values()) {
    if (handle.kind === 'directory') {
      out.push({ name: handle.name, dir: true, size: 0, modified: 0, directory: handle })
      continue
    }
    try {
      const file = await handle.getFile()
      out.push({
        name: handle.name,
        dir: false,
        size: file.size,
        modified: file.lastModified,
        file: handle,
      })
    } catch {
      // A file that vanished between the listing and the read is not an error
      // worth stopping a whole directory for; it is reported as unreadable by
      // being absent, which is what it now is.
    }
  }
  return out
}

/**
 * Streams a response body into a file in the chosen folder.
 *
 * This is what makes the local pane a pane rather than a download button: the
 * bytes land where the operator is looking, under the name they are looking at,
 * without going through the browser's download folder and back.
 *
 * `onProgress` is fed from the stream rather than from Content-Length, because
 * the length is a claim and the bytes are the fact.
 */
export async function saveIntoLocalDir(
  dir: LocalDirHandle,
  name: string,
  body: ReadableStream<Uint8Array>,
  onProgress?: (bytes: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true })
  const writable = await handle.createWritable()
  const reader = body.getReader()
  let written = 0
  try {
    for (;;) {
      // Checked here as well as on the fetch. Aborting the request rejects the
      // reader eventually, but the writable handle is this function's to close,
      // and letting that rejection escape without abort() is the one path that
      // leaves a truncated file on the operator's own disk.
      if (signal?.aborted) throw new DOMException('cancelled', 'AbortError')
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        await writable.write(value)
        written += value.byteLength
        onProgress?.(written)
      }
    }
    await writable.close()
  } catch (err) {
    // A half-written file at the destination is the failure the whole transfer
    // discipline exists to prevent, on this side as much as on the host's.
    await writable.abort?.()
    throw err
  }
}

/** One node of a local subtree, named relative to the directory that was walked. */
export interface LocalTreeNode {
  rel: string
  dir: boolean
  size: number
  file?: LocalFileHandle
}

/**
 * Walks a local folder so it can be sent as a folder.
 *
 * Breadth-first and bounded. The bound is not politeness: this is `getFile()`
 * once per entry — the only way the API offers a size — so an accidental pick of
 * a `node_modules` would otherwise freeze the tab with no way to say why. The
 * caller is told it was cut and refuses to send a partial tree, the same rule the
 * host side follows.
 *
 * Directories are emitted *before* anything inside them, which is what lets the
 * receiver create each one before writing into it.
 */
export const MAX_LOCAL_TREE = 50000

export async function walkLocalDir(
  dir: LocalDirHandle,
  max: number = MAX_LOCAL_TREE,
): Promise<{ nodes: LocalTreeNode[]; truncated: boolean }> {
  const nodes: LocalTreeNode[] = []
  const queue: Array<{ handle: LocalDirHandle; prefix: string }> = [{ handle: dir, prefix: '' }]

  while (queue.length > 0) {
    const { handle, prefix } = queue.shift()!
    for await (const child of handle.values()) {
      if (nodes.length >= max) return { nodes, truncated: true }
      const rel = prefix === '' ? child.name : `${prefix}/${child.name}`
      if (child.kind === 'directory') {
        nodes.push({ rel, dir: true, size: 0 })
        queue.push({ handle: child, prefix: rel })
        continue
      }
      try {
        const f = await child.getFile()
        nodes.push({ rel, dir: false, size: f.size, file: child })
      } catch {
        // Vanished between the walk and the read. Absent is what it now is.
      }
    }
  }
  return { nodes, truncated: false }
}

/**
 * Resolves a relative directory path under a handle, creating what is missing.
 *
 * Unlike the host side — where creating a chain nobody named is refused on
 * purpose — this creates every level, because here the levels *were* named: they
 * came out of a walk of the remote tree, and the operator asked for that tree.
 */
export async function ensureLocalPath(dir: LocalDirHandle, rel: string): Promise<LocalDirHandle> {
  let at = dir
  for (const part of rel.split('/')) {
    if (part === '' || part === '.') continue
    at = await at.getDirectoryHandle(part, { create: true })
  }
  return at
}

/** Joins a local path for display. The local pane never sends a path anywhere —
 *  the API takes handles — so this is a label and nothing more. */
export function localPathLabel(root: string, trail: string[]): string {
  return [root, ...trail].join('/')
}
